import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onIdReady, pc_id, remoteSocket } from "../services/socket";
import { useBranding } from "../branding/BrandingContext";

const SelectGame: React.FC = () => {
    const branding = useBranding();
    const navigate = useNavigate();
    const [mousePos, setMousePos] = useState({ x: -50, y: -50 });
    const [isHoveringGame, setIsHoveringGame] = useState(false);
    const [deviceId, setDeviceId] = useState(() => pc_id || '');

    useEffect(() => onIdReady(setDeviceId), []);
    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            setMousePos({ x: event.clientX, y: event.clientY });
        };

        const resolveGameUrl = (game: string) => {
            switch (game) {
                case 'aviator':
                    return 'https://aviator.pushvault.shop/game';
                case 'pilot':
                    return "https://game.pushvault.shop/game";
                case 'fury':
                    return 'http://173.249.23.118:8089/';
                case 'nganya':
                    return 'https://nganya.pushvault.shop/game';
                case 'kanyonde':
                    return 'https://kanyonde.pushvault.shop';
                case 'chicken-road':
                    return 'https://chickenroad.pushvault.shop';
                case 'sugar-daddy':
                    return 'https://sugar.pushvault.shop/';
                default:
                    return null;
            }
        };

        const handleSetGame = (data: any) => {
            const targetGame = data.currentGame;
            if (!targetGame) return;

            if (targetGame === 'both') {
                localStorage.setItem('selected_game', 'both');
                navigate('/select-game');
                return;
            }

            localStorage.setItem('selected_game', targetGame);
            const targetUrl = resolveGameUrl(targetGame);
            if (targetUrl) {
                window.location.href = targetUrl;
            } else {
                navigate('/select-game');
            }
        };


        window.addEventListener('mousemove', handleMouseMove);
        remoteSocket.on('setGame', handleSetGame);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            remoteSocket.off('setGame', handleSetGame);
        };
    }, [navigate]);

    const handleNavigation = (id: string) => {
        localStorage.setItem('selected_game', id);
        if (id === 'aviator') {
            window.location.href = 'https://aviator.pushvault.shop/game';
        } else if (id === 'fury') {
            window.location.href = 'http://173.249.23.118:8089/';
        } else if (id === 'pilot') {
            window.location.href = "https://game.pushvault.shop/game";
        } else if (id === 'nganya') {
            window.location.href = "https://nganya.pushvault.shop/game";
        } else if (id === 'kanyonde') {
            window.location.href = "https://kanyonde.pushvault.shop/game";
        } else if (id === 'chicken-road') {
            window.location.href = "https://chickenroad.pushvault.shop";
        } else if (id === 'sugar-daddy') {
            window.location.href = 'https://sugar.pushvault.shop/';
        } else {
            navigate('/select-game');
        }
    };

    return (
        <div className="relative flex h-screen min-h-0 flex-col items-center overflow-hidden bg-[#080808] px-5 py-5 text-white selection:bg-red-600 md:px-8 md:py-6 font-sans cursor-none">

            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
                style={{ backgroundImage: "url('/launcher-background.jpg')" }}
            />
            <div className="absolute inset-0 bg-black/65 pointer-events-none" />

            <div
                className="fixed pointer-events-none z-[9999] left-0 top-0"
                style={{
                    transform: `translate3d(${mousePos.x}px, ${mousePos.y}px, 0) scale(${isHoveringGame ? 1.35 : 1})`,
                    transition: 'transform 100ms ease-out'
                }}
                aria-hidden="true"
            >
                <svg
                    width="34"
                    height="34"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`-translate-x-[3px] -translate-y-[3px] transition-colors duration-200 drop-shadow-[0_0_10px_rgba(220,38,38,0.85)] ${isHoveringGame ? 'text-red-500' : 'text-white'}`}
                >
                    <path d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z" fill="currentColor" />
                </svg>
            </div>

            {/* Dynamic Background Elements */}
            <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-red-600/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-red-900/10 rounded-full blur-[120px] pointer-events-none" />

            {/* Header Section */}
            <div className="relative z-10 mt-4 shrink-0 text-center space-y-1.5">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/[0.04] border border-white/10 rounded-full mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
                    <span className="text-white/60 text-[10px] font-bold tracking-[0.24em] uppercase">Launcher ready</span>
                </div>
                <h1 className="text-4xl md:text-6xl font-black tracking-[-0.055em] uppercase italic leading-none">
                    Choose your <span className="text-red-600 drop-shadow-[0_0_18px_rgba(220,38,38,0.35)]">game</span>
                </h1>
                <p className="text-sm text-white/45 max-w-xl mx-auto">Pick an experience to launch. You can return here anytime to switch games.</p>
            </div>

            <div className="relative z-10 mx-24 mt-3 mb-14 flex min-h-0 w-[calc(100%-12rem)] flex-1 flex-wrap justify-center gap-3 lg:gap-4">
                {[
                    {
                        id: 'aviator',
                        title: 'AVIATOR',
                        icon: '/aviator_icon-optimized.jpg'
                    },
                    {
                        id: 'pilot',
                        title: 'PILOT',
                        icon: '/launcherBg-optimized.jpg'
                    },
                    {
                        id: 'fury',
                        title: 'FURY',
                        icon: '/fury_icon.png'
                    },
                    {
                        id: 'nganya',
                        title: 'NGANYA',
                        icon: '/nganya_icon.jpg'
                    },
                    {
                        id: 'kanyonde',
                        title: 'AVIATOR (2)',
                        icon: '/kanyonde_aviator_icon.jpg'
                    },
                    {
                        id: 'chicken-road',
                        title: 'CHICKEN ROAD',
                        icon: '/chicken_road_icon.jpg'
                    },
                    {
                        id: 'sugar-daddy',
                        title: 'SUGAR DADDY',
                        icon: '/sugar-daddy.png'
                    }
                ].map((game, index) => (
                    <button
                        key={game.id}
                        onClick={() => handleNavigation(game.id)}
                        onMouseEnter={() => setIsHoveringGame(true)}
                        onMouseLeave={() => setIsHoveringGame(false)}
                        aria-label={`Launch ${game.title}`}
                        className={`group relative h-[calc(50%-0.5rem)] min-h-0 flex-none overflow-hidden rounded-xl border border-white/10 bg-[#151515] text-left shadow-[0_10px_25px_rgba(0,0,0,0.35)] transition duration-200 hover:-translate-y-0.5 hover:border-red-500/70 hover:shadow-[0_14px_32px_rgba(220,38,38,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 cursor-none ${index < 4 ? 'basis-[calc(25%-0.75rem)]' : 'basis-[calc(33.333%-0.75rem)]'}`}
                    >
                        <img
                            src={game.icon}
                            alt={game.title}
                            className="h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/15 to-transparent" />
                        <div className="absolute right-0 bottom-0 left-0 p-3">
                            <strong className="block truncate text-sm font-black tracking-wide uppercase italic drop-shadow-lg xl:text-base">
                                {game.title}
                            </strong>
                            <span className="mt-1 block text-[9px] font-bold tracking-[0.25em] text-red-400 uppercase">
                                Play now
                            </span>
                        </div>
                    </button>
                ))}
            </div>

            {/* Footer */}
            <div
                aria-label={`Device ID: ${deviceId || 'Awaiting device ID'}`}
                className="pointer-events-none fixed bottom-4 left-4 z-[2147483646] max-w-[calc(100vw-2rem)] truncate rounded-full border border-white/25 bg-black/65 px-5 py-2.5 font-mono text-base font-bold tracking-[0.1em] text-white/80 shadow-xl backdrop-blur-sm select-none"
            >
                {deviceId || 'Awaiting device ID...'}
            </div>

            <div className="relative z-10 flex shrink-0 items-center gap-3 text-white/25">
                <div className="h-px w-10 bg-white/20" />
                <div className="text-[9px] uppercase tracking-[0.28em] font-bold">
                    {branding.appName} · v{branding.version} · Secure connection
                </div>
                <div className="h-px w-10 bg-white/20" />
            </div>

        </div>
    );
};

export default SelectGame;
